import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Paperclip, Loader2, Save, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDateTime } from "@/lib/format";
import { CLAIM_STATUS_FLOW, CLAIM_STATUS_META, REASON_LABEL } from "@/lib/claims";

const ALL_STATUSES = [...CLAIM_STATUS_FLOW, "denied"];

function ClaimStatusBadge({ status }) {
  const meta = CLAIM_STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>{meta.label}</span>;
}

export default function AdminClaims({ accountId = null }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [managing, setManaging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = accountId ? `?account_id=${accountId}` : "";
      const { data } = await api.get(`/claims${qs}`);
      setClaims(data.claims || []);
    } catch (e) {
      toast.error("Could not load claims");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const filtered = useMemo(
    () => (filter === "all" ? claims : claims.filter((c) => c.status === filter)),
    [claims, filter]
  );

  const openCount = claims.filter((c) => !["credit_applied", "denied"].includes(c.status)).length;
  const navigate = useNavigate();

  return (
    <div data-testid="admin-claims" className="space-y-4">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-900 leading-relaxed flex-1">
          Review customer claims, move them through the workflow, and record an approved credit amount.
          {openCount > 0 && <strong> {openCount} open claim{openCount === 1 ? "" : "s"} awaiting action.</strong>}
        </p>
        <button onClick={() => navigate("/training?topic=claims")} data-testid="open-training-claims" className="inline-flex items-center gap-1.5 text-sm font-semibold text-bh-blue hover:text-bh-blue-dark shrink-0"><BookOpen className="w-4 h-4" /> How-to guide</button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} data-testid="claim-filter-all"
          className={`text-xs font-semibold px-3 py-1.5 rounded-md ${filter === "all" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          All ({claims.length})
        </button>
        {ALL_STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`claim-filter-${s}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md ${filter === s ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {CLAIM_STATUS_META[s].label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading claims…</div>
        ) : (
          <table className="w-full text-left" data-testid="admin-claims-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 font-medium">Order #</th>
                <th className="py-3 px-4 font-medium">Store</th>
                <th className="py-3 px-4 font-medium">Reason</th>
                <th className="py-3 px-4 font-medium">Filed</th>
                <th className="py-3 px-4 font-medium">Values</th>
                <th className="py-3 px-4 font-medium">Files</th>
                <th className="py-3 px-4 font-medium">Credit</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`admin-claim-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-3 px-4 text-sm font-semibold text-slate-900">{c.order_number}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{c.company_name || "—"}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 capitalize">{REASON_LABEL(c.reason)}</td>
                  <td className="py-3 px-4 text-sm text-slate-500">{formatDate(c.created_at)}</td>
                  <td className="py-3 px-4 text-xs text-slate-600">WS ${(c.wholesale_value || 0).toFixed(0)} · Sold ${(c.sold_value || 0).toFixed(0)}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{c.attachments?.length || 0}</td>
                  <td className="py-3 px-4 text-sm font-semibold text-emerald-700">{c.credit_amount > 0 ? `$${c.credit_amount.toFixed(2)}` : "—"}</td>
                  <td className="py-3 px-4"><ClaimStatusBadge status={c.status} /></td>
                  <td className="py-3 px-4 text-right">
                    <button data-testid={`manage-claim-${c.id}`} onClick={() => setManaging(c)}
                      className="text-blue-600 hover:text-blue-700 text-xs font-semibold">Manage</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-slate-500">No claims{filter !== "all" ? " with this status" : ""} yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {managing && (
        <ManageClaimDialog claim={managing} onClose={() => setManaging(null)} onSaved={() => { setManaging(null); load(); }} />
      )}
    </div>
  );
}

function ManageClaimDialog({ claim, onClose, onSaved }) {
  const [status, setStatus] = useState(claim.status);
  const [credit, setCredit] = useState(claim.credit_amount || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { note: note.trim() || undefined };
      if (status !== claim.status) payload.status = status;
      if (Number(credit) !== (claim.credit_amount || 0)) payload.credit_amount = Number(credit) || 0;
      await api.patch(`/admin/claims/${claim.id}`, payload);
      toast.success("Claim updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="manage-claim-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>
            Claim · {claim.order_number}
          </DialogTitle>
          <DialogDescription>Review the claim, move it through the workflow, and record an approved credit.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1 bg-slate-50 border border-slate-200 rounded-lg p-4 mt-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Store</span><span className="font-medium text-slate-900">{claim.company_name || "—"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Reason</span><span className="font-medium text-slate-900 capitalize">{REASON_LABEL(claim.reason)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Wholesale / Sold</span><span className="font-medium text-slate-900">${(claim.wholesale_value || 0).toFixed(2)} / ${(claim.sold_value || 0).toFixed(2)}</span></div>
          {claim.tracking_number && <div className="flex justify-between"><span className="text-slate-500">Tracking</span><span className="font-medium text-slate-900">{claim.tracking_number}</span></div>}
        </div>

        {claim.description && (
          <div className="mt-3">
            <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1">Description</p>
            <p className="text-sm text-slate-700">{claim.description}</p>
          </div>
        )}

        {claim.attachments?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Attachments</p>
            <div className="flex flex-wrap gap-2">
              {claim.attachments.map((a) => (
                <a key={a.id} href={`${API_BASE}/claims/files/${a.storage_path}`} target="_blank" rel="noreferrer"
                  data-testid={`admin-claim-attachment-${a.id}`}
                  className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-700 rounded-md px-2.5 py-1.5 font-medium transition-colors">
                  <Paperclip className="w-3.5 h-3.5" /> {a.original_filename}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Status</label>
            <select data-testid="manage-claim-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40">
              {ALL_STATUSES.map((s) => <option key={s} value={s}>{CLAIM_STATUS_META[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Approved credit ($)</label>
            <input data-testid="manage-claim-credit" type="number" step="0.01" min="0" value={credit} onChange={(e) => setCredit(e.target.value)}
              placeholder="0.00" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Note (visible to customer)</label>
          <textarea data-testid="manage-claim-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="Optional update, e.g. 'Filed with UPS, ref #12345'"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        </div>

        {claim.status_history?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">History</p>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {claim.status_history.map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-xs text-slate-600">
                  <span className="text-slate-400">{formatDateTime(h.at)}</span> · <span className="font-medium">{CLAIM_STATUS_META[h.status]?.label || h.status}</span>
                  {h.note ? ` — ${h.note}` : ""} <span className="text-slate-400">({h.by})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button data-testid="save-claim-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
